import { View, ViewOptions } from 'backbone.marionette'
import '@/backbone.stickit-marionette'
import select2Delimited from '@/ui/dropdown/select2-delimited'
// @ts-expect-error TODO: Convert to TypeScript
import ProductModel from '../models/product'
import { showRequestProductQuoteModal } from '@/catalog/product-cta-buttons'

export default class AddToCartQuantityView extends View<ProductModel> {
  constructor(options: ViewOptions<ProductModel>) {
    super({
      ...options,
      ui: {
        select: 'select'
      },
      // Trigger a change so that if this quantity is a related suite item, we know to recalculate the subtotal.
      // Might be better to do this elsewhere.
      events: {
        change: 'notifyChange'
      }
    })
    this.bindings = {
      '@ui.select': {
        observe: 'quantity',
        // Turn the model number into a string, to match the option[value] string in the view.
        onGet(valueInt: number): string {
          return `${valueInt}`
        },
        onSet: (quantity: number): number => {
          const item = this.model.get('item')
          // 0 is allowed in cart if there are recipients, but it shouldn't be selectable from the product page
          const minQuantity = Math.max(
            item.get('minQuantity'),
            item.get('quantityIncrement')
          )
          const maxQuantity = item.get('maxQuantity')
          /**
           * Sets the select value, triggers a change event, and returns the value.
           * If the value is being changed after getting set, this will sync
           * the `<select>` value and any event listeners with the model state.
           */
          const updateSelectValue = (value: number): number => {
            this.getUI('select').val(value).trigger('change')
            return value
          }

          if (quantity === -1) {
            // -1 is a special value for > max, which is used to trigger a modal to get a quote
            showRequestProductQuoteModal(item.toJSON())
            // Set the highest quantity to allow the user to proceed to Personalize without friction
            return updateSelectValue(maxQuantity)
          } else if (quantity < minQuantity) {
            // TODO: Consider removing.
            //   This should be impossible via stickit, but leaving this in case there's an undocumented edge case.
            // Prevent invalid quantities from persisting if triggered from stickit
            return updateSelectValue(minQuantity)
          } else {
            return quantity
          }
        },
        // Prevent the view from updating, so our data-* attributes aren't removed
        updateView: false,
        // Turn the view string into a number, to set a number into the model in onSet
        getVal($el: JQuery<HTMLElement>): number {
          return parseInt($el.val() as string)
        }
      }
    }

    // View takes precedence over model. At initialization time, set the model to whatever the view has.
    // This allows our model to parse whatever the user selected before our JavaScript initializes.
    const $selected = this.getUI('select').find(':selected')
    const quantity = parseInt($selected.val() as string)
    // Can't check minQuantity here because async model data hasn't initialized yet
    this.model.set('quantity', quantity < 0 ? 0 : quantity, {
      silent: true
    })

    // We can't use the behavior because onRender() doesn't actually execute for a pre-rendered View.
    this.stickit()

    this.renderPriceDelimiter()
    // Re-render the quantity dropdown whenever the discountPercentage attributes changes on the model. This allows us
    // to re-render the quantity dropdown with the appropriate discounts if the quantity dropdown is shown before the
    // the item is fetched and loaded (i.e. discountPercentage is has not been fetched).
    this.listenTo(
      this.model.get('item'),
      'change:discountPercentage',
      this.renderPriceDelimiter
    )
  }

  notifyChange() {
    this.model.trigger('change')
  }

  renderPriceDelimiter() {
    select2Delimited.priceDelimiter(
      this.getUI('select'),
      this.model.get('item').get('discountPercentage')
    )
  }
}
